import { ComponentPropsWithRef, KeyboardEvent, MouseEvent } from "react";
import { Pencil } from "lucide-react";

import { Badge } from "@/components/_atoms/badge";
import { Button } from "@/components/_atoms/button";
import { TableRow } from "@/components/_atoms/table";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { formatMunicipalPropertyAddress } from "@/domain/municipal-property/lib/format-municipal-property-address";
import { MunicipalPropertyCatalogRow } from "@/domain/municipal-property/models/municipal-property-catalog-row.model";
import { cn } from "@/lib/cn";

import { MunicipalPropertyTableCell } from "./municipal-property-table-cell";

interface MunicipalPropertyCatalogTableRowProps {
  property: MunicipalPropertyCatalogRow;
  rowProps?: ComponentPropsWithRef<typeof TableRow>;
  onManage?: (property: MunicipalPropertyCatalogRow) => void;
}

export function MunicipalPropertyCatalogTableRow({
  property,
  rowProps,
  onManage,
}: MunicipalPropertyCatalogTableRowProps) {
  const { t, i18n } = useDomainTranslation("municipal-property");

  function handleRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>): void {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onManage?.(property);
    }
  }

  return (
    <TableRow
      {...rowProps}
      data-testid="municipal-property-row"
      tabIndex={onManage ? 0 : undefined}
      aria-label={onManage ? `${t("catalog.actions.manage")}: ${property.name}` : undefined}
      className={cn(
        onManage && "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
      )}
      onClick={onManage ? (): void => onManage(property) : undefined}
      onKeyDown={onManage ? handleRowKeyDown : undefined}
    >
      <MunicipalPropertyTableCell className="font-medium">{property.name}</MunicipalPropertyTableCell>
      <MunicipalPropertyTableCell>{t(`type.${property.type}`)}</MunicipalPropertyTableCell>
      <MunicipalPropertyTableCell>
        {property.municipalityList.length === 0 ? (
          <span className="text-muted-foreground">{t("catalog.workspaceWide")}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {property.municipalityList.map((municipality) => (
              <Badge key={municipality.id} variant="outline" className="px-1.5 py-0.5 font-normal">
                {municipality.name}
              </Badge>
            ))}
          </span>
        )}
      </MunicipalPropertyTableCell>
      <MunicipalPropertyTableCell className="text-muted-foreground">
        {formatMunicipalPropertyAddress(property) || t("catalog.emptyCell")}
      </MunicipalPropertyTableCell>
      <MunicipalPropertyTableCell className="text-right tabular-nums">
        {property.usableFloorAreaM2?.toLocaleString(i18n.language) ?? t("catalog.emptyCell")}
      </MunicipalPropertyTableCell>
      <MunicipalPropertyTableCell className="text-right tabular-nums">
        {property.commissioningYear ?? t("catalog.emptyCell")}
      </MunicipalPropertyTableCell>
      <MunicipalPropertyTableCell>
        <Badge variant={property.archivedAt ? "secondary" : "default"}>
          {property.archivedAt ? t("catalog.state.archived") : t("catalog.state.active")}
        </Badge>
      </MunicipalPropertyTableCell>
      {onManage ? (
        <MunicipalPropertyTableCell className="text-right">
          <Button
            variant="ghost"
            size="icon"
            className="size-7 [&_svg]:size-3.5"
            aria-label={t("catalog.actions.manage")}
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              event.stopPropagation();
              onManage(property);
            }}
          >
            <Pencil data-icon="inline-start" aria-hidden="true" />
          </Button>
        </MunicipalPropertyTableCell>
      ) : null}
    </TableRow>
  );
}
